import { useCallback, useEffect, useMemo, useState } from "react";
import { GitMerge, History, Loader2 } from "lucide-react";
import { api, apiError, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

/** Owner merges a duplicate customer into the one they want to keep. Nothing is deleted. */
export function MergeCustomersDialog({ customers, onDone }) {
  const [open, setOpen] = useState(false);
  const [primary, setPrimary] = useState("");
  const [duplicate, setDuplicate] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const options = useMemo(
    () => customers.map((c) => ({ id: c.id, label: `${c.company}${c.code ? ` (${c.code})` : ""}` })),
    [customers]);

  useEffect(() => {
    setPreview(null);
    if (!primary || !duplicate || primary === duplicate) return;
    api.post("/customer-admin/merge/preview", { primary_id: primary, duplicate_id: duplicate })
      .then(({ data }) => setPreview(data))
      .catch((e) => toast.error(apiError(e)));
  }, [primary, duplicate]);

  const run = async () => {
    if (reason.trim().length < 5) return toast.error("Please write why — it is kept as proof");
    setBusy(true);
    try {
      const { data } = await api.post("/customer-admin/merge",
        { primary_id: primary, duplicate_id: duplicate, reason: reason.trim() });
      const moved = Object.entries(data.moved || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Merged into ${data.primary}${moved ? ` • moved ${moved}` : ""}`);
      setOpen(false);
      setPrimary(""); setDuplicate(""); setReason(""); setPreview(null);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="merge-customers-button">
          <GitMerge className="mr-1 h-4 w-4" /> Merge duplicates
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="merge-customers-dialog">
        <DialogHeader>
          <DialogTitle>Merge duplicate customers</DialogTitle>
          <DialogDescription>
            Every quotation, invoice, payment and document moves to the customer you keep. The
            duplicate is archived, never deleted, so a merge can be undone by restoring it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Keep this customer">
            <Select value={primary} onValueChange={setPrimary}>
              <SelectTrigger data-testid="merge-primary-select">
                <SelectValue placeholder="Select the customer to keep" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Merge this duplicate into it">
            <Select value={duplicate} onValueChange={setDuplicate}>
              <SelectTrigger data-testid="merge-duplicate-select">
                <SelectValue placeholder="Select the duplicate" />
              </SelectTrigger>
              <SelectContent>
                {options.filter((o) => o.id !== primary).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {preview && (
            <div className="rounded border border-border p-3 text-sm" data-testid="merge-preview">
              <div className="font-medium">
                {preview.duplicate.company} → {preview.primary.company}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {Object.keys(preview.moving || {}).length === 0
                  ? "No linked records to move."
                  : `Moving ${Object.entries(preview.moving)
                    .map(([k, v]) => `${v} ${k}`).join(", ")}.`}
              </div>
              {preview.fields_to_fill?.length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Filling blank details: {preview.fields_to_fill.join(", ")}.
                </div>
              )}
            </div>
          )}
          <Field label="Reason (kept as proof)">
            <Input data-testid="merge-reason" value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Same company entered twice" />
          </Field>
        </div>
        <DialogFooter>
          <Button disabled={busy || !preview} onClick={run} data-testid="merge-confirm">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Merge customers
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Every customer change: who asked, what changed, who decided, when. */
export function CustomerChangeHistory() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer-admin/history?action=${filter}`);
      setRows(data.history);
      setActions(data.actions);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid gap-3" data-testid="customer-change-history">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-muted-foreground" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="history-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All activity</SelectItem>
            {actions.map((a) => (
              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <Empty title="No activity yet" hint="Edits, archives, restores and merges appear here." />
      ) : (
        <div className="surface overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>What happened</TableHead>
                <TableHead>Asked by</TableHead>
                <TableHead>Decided by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`history-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {shortDate(r.at)}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{r.company}</TableCell>
                  <TableCell className="text-sm">
                    <Badge variant="outline" className="mr-1 text-[10px]">{r.action_label}</Badge>
                    {r.via === "email-link" && (
                      <Badge variant="secondary" className="mr-1 text-[10px]">one-tap link</Badge>
                    )}
                    {(r.changes || []).map((c) => (
                      <div key={c.field} className="text-xs text-muted-foreground">
                        {c.label}: {String(c.current || "—")} → {String(c.proposed || "—")}
                      </div>
                    ))}
                    {r.reason && (
                      <div className="text-xs text-muted-foreground">Reason: {r.reason}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.requested_by || "-"}</TableCell>
                  <TableCell className="text-sm">{r.decided_by || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
